/**
 * advisor-normalize.js — one canonical record per quoted plan
 * (spec docs/superpowers/specs/2026-10-12-plan-advisor-design.md, "Browser modules").
 *
 * Pure. The eight quoting sources each push their own shape into allPlansWithPremiums (only id,
 * name, category, network, deductible and calculatedPremium are common). This module reads each
 * source's real keys — the same keys its client writes — into one record the advisor can score.
 * A value the source does not carry stays null: nothing is invented. That is the difference from
 * plan-classify.js getOopMax/isHsaEligible, which fall back to guesses for display.
 *
 * The fixtures in tests/modules/advisor-normalize.test.js are built in each client's own output
 * shape (api.js, cms-api.js, pivot-api.js, uhone-api.js, manhattan-life-api.js, flex-api.js,
 * data.js, iron-health-data.js) and pin every canonical field per source.
 */

import { getProductType, carrierDisplayLabel } from './plan-classify.js';

// CMS moops types that hold the individual in-network total (same pair plan-classify reads).
const CMS_MOOP_TYPES = [
  'Maximum Out of Pocket for Medical and Drug EHB Benefits (Total)',
  'Individual Medical',
];

export function detectSource(plan) {
  if (!plan || typeof plan !== 'object') return 'unknown';
  const id = String(plan.id == null ? '' : plan.id);
  if (plan._cmsData || String(plan.type || '').toLowerCase() === 'aca') return 'cms';
  if (plan._pivotData || id.startsWith('pivot-')) return 'pivot';
  if (plan._uhoneData || id.startsWith('uhone-')) return 'uhone';
  if (plan._manhattanData || id.startsWith('manhattan-')) return 'manhattan';
  if (plan._flexData || id.startsWith('flex-')) return 'flex';
  if (plan._apiData || plan.carrier === 'Allstate Health') return 'ngah';
  if (plan.carrier === 'Iron Health' || id.startsWith('IH-')) return 'ironhealth';
  if (/lifex/i.test(plan.category || '')) return 'lifex';
  return 'unknown';
}

function num(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** First dollar amount in a carrier string ("$6,000 / $12,000" → 6000). Percentages are not money. */
export function parseMoney(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const m = /\$?\s*(\d[\d,]*(?:\.\d+)?)(?!\s*%)(?![\d,.])/.exec(v);
  if (!m) return null;
  const n = Number(m[1].replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function cmsOopMax(plan) {
  const moops = plan.planDetails && Array.isArray(plan.planDetails.moops) ? plan.planDetails.moops : [];
  for (const type of CMS_MOOP_TYPES) {
    const rows = moops.filter((m) => m && m.type === type);
    const row = rows.find((m) => /individual/i.test(m.family_cost || '') && /in-network/i.test(m.network_tier || ''))
      || rows.find((m) => /individual/i.test(m.family_cost || ''))
      || rows[0];
    if (row && num(row.amount) != null) return num(row.amount);
  }
  return null;
}

function oopMaxOf(plan, source) {
  const pd = plan.planDetails || {};
  switch (source) {
    case 'cms': return cmsOopMax(plan);
    case 'pivot':
    case 'uhone': return parseMoney(pd.moop);
    case 'ironhealth': {
      const benefits = plan.benefits && typeof plan.benefits === 'object' ? plan.benefits : {};
      const key = Object.keys(benefits).find((k) => /out of pocket max/i.test(k) && !/out[- ]of[- ]net/i.test(k));
      return key ? parseMoney(benefits[key]) : null;
    }
    default: return null;
  }
}

export function normalizeIssueType(plan, source) {
  if (source === 'cms') return 'aca';
  const raw = String((plan.planDetails && plan.planDetails.issueType) || plan.issueType || '');
  if (/guarantee/i.test(raw)) return 'guaranteed';
  if (/simplified/i.test(raw)) return 'simplified';
  if (/underwrit|medical/i.test(raw)) return 'underwritten';
  return 'unknown';
}

function networkTypeOf(plan) {
  const s = String(plan.network || '').toUpperCase();
  const m = /\b(HMO|EPO|PPO|POS)\b/.exec(s);
  return m ? m[1].toLowerCase() : null;
}

function hsaOf(plan, source) {
  const pd = plan.planDetails || {};
  if (typeof pd.hsa_eligible === 'boolean') return pd.hsa_eligible;
  if (source === 'lifex' && String(plan.type || '').toLowerCase() === 'hsa') return true;
  if (source === 'ironhealth' && /\bHSA\b/.test(`${plan.groupFamily || ''} ${plan.name || ''}`)) return true;
  return null;
}

// LifeX and Iron Health price from age bands keyed "18-29"…"55-64" and clamp ages past the top
// band to its price, so the top band's upper bound is the oldest age they can actually quote.
function bandMaxAge(plan) {
  if (!plan.premiums || typeof plan.premiums !== 'object') return null;
  const tops = Object.keys(plan.premiums)
    .map((k) => /^(\d+)\s*-\s*(\d+)$/.exec(k))
    .filter(Boolean)
    .map((m) => Number(m[2]));
  return tops.length ? Math.max(...tops) : null;
}

function agesOf(plan, source) {
  const pd = plan.planDetails || {};
  if (source === 'flex') {
    return { minAge: num(pd.minAge) > 0 ? num(pd.minAge) : null, maxAge: num(pd.maxAge) > 0 ? num(pd.maxAge) : null };
  }
  if (source === 'lifex' || source === 'ironhealth') return { minAge: null, maxAge: bandMaxAge(plan) };
  return { minAge: null, maxAge: null };
}

function termMonthsOf(plan, source) {
  const pd = plan.planDetails || {};
  if (source === 'cms') return 12;
  if (source === 'ngah') {
    if (pd.stmConsecutiveTerms === 2) return 24;
    if (pd.stmRenewalTerms !== undefined) return (num(pd.stmTermMonths) || 12) * ((num(pd.stmRenewalTerms) || 0) + 1);
    return null;
  }
  if (source === 'uhone') return num(pd.termMonths) > 0 ? num(pd.termMonths) : null;
  return null;
}

/**
 * @param {Object} plan - one entry of allPlansWithPremiums
 * @returns {Object} canonical record; `planId` is String(plan.id)
 */
export function normalizePlan(plan) {
  const p = plan && typeof plan === 'object' ? plan : {};
  const pd = p.planDetails || {};
  const source = detectSource(p);
  const premium = num(p.calculatedPremium);
  const deductible = num(p.deductible);
  const oopMax = oopMaxOf(p, source);
  const coinsurance = p.coinsurance != null ? String(p.coinsurance)
    : (pd.coinsurance != null && pd.coinsurance !== '' ? String(pd.coinsurance) : null);
  const { minAge, maxAge } = agesOf(p, source);
  const states = Array.isArray(p.availableStates) && p.availableStates.length ? p.availableStates.slice() : null;
  return {
    planId: String(p.id),
    name: String(p.name || ''),
    source,
    carrier: carrierDisplayLabel(p),
    productType: getProductType(p),
    premium,
    premiumUnsubsidised: source === 'cms' && num(p.premiumWithoutSubsidy) != null ? num(p.premiumWithoutSubsidy) : premium,
    deductible,
    oopMax,
    coinsurance,
    networkType: networkTypeOf(p),
    metalLevel: source === 'cms' && p.metalLevel ? String(p.metalLevel).toLowerCase() : null,
    issueType: normalizeIssueType(p, source),
    hsaEligible: hsaOf(p, source),
    minAge,
    maxAge,
    termMonths: termMonthsOf(p, source),
    states,
    detailLevel: deductible != null && oopMax != null ? 'full' : 'limited',
  };
}

export const AdvisorNormalize = { detectSource, parseMoney, normalizeIssueType, normalizePlan };
